import { useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Icon,
  Text,
  Box,
  useColorModeValue,
  Flex,
} from "@chakra-ui/react";
import { DownloadIcon } from "@chakra-ui/icons";
import { Button as MetriportButton } from "../../button";
import { formatDate } from "../shared";
import { useDownloadFile } from "../../../shared-logic/useDownloadFile";

const DEFAULT_DATAPOINTS = 10;

function getConversionTypeByFileName(fileName: string): "html" | undefined {
  if (fileName.endsWith(".hl7")) return undefined;
  return "html";
}

type DataPoint = {
  id?: string;
  filename?: string;
  date: string;
  value: string;
};

interface DataPointsDownloadableTableProps {
  title: string;
  dataPoints: DataPoint[];
}

export function DataPointsDownloadableTable({
  title,
  dataPoints,
}: DataPointsDownloadableTableProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { isDownloading, onDownloadFile } = useDownloadFile();

  const content = dataPoints.slice(0, isExpanded ? dataPoints.length : DEFAULT_DATAPOINTS);

  const handleDownload = (point: DataPoint) => {
    if (!point.id || !point.filename) return;

    onDownloadFile({
      docId: point.id,
      docFilename: point.filename,
      conversionType: getConversionTypeByFileName(point.filename),
    });
  };

  const isLargerThanDefault = (numItems: number): boolean => {
    return numItems > DEFAULT_DATAPOINTS;
  };

  return (
    <Box mb={3}>
      <Flex justifyContent="space-between" alignItems="center" mb={2}>
        <Text fontSize={"2xl"} fontWeight="bold">
          {title}:
        </Text>
        {isLargerThanDefault(dataPoints.length) && (
          <MetriportButton onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? "See Less" : "View All"}
          </MetriportButton>
        )}
      </Flex>
      <Box
        px={4}
        py={2}
        border="solid"
        borderRadius={10}
        borderColor={useColorModeValue("gray.200", "gray.700")}
      >
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Date</Th>
              <Th>Value</Th>
              <Th>Source</Th>
            </Tr>
          </Thead>
          <Tbody>
            {content.map((point, index) => {
              return (
                <Tr key={index}>
                  <Td fontWeight={index === 0 ? "bold" : "normal"}>{formatDate(point.date)}</Td>
                  <Td fontWeight={index === 0 ? "bold" : "normal"}>{point.value}</Td>
                  <Td>
                    {point.id && point.filename && (
                      <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<Icon as={DownloadIcon} />}
                        onClick={() => handleDownload(point)}
                        isLoading={isDownloading[point.id]?.downloading ?? false}
                        isDisabled={!point.filename}
                      >
                        View
                      </Button>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}
